import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Building2, Crown, Power, RotateCw, Shield, Plus, Key, X, Edit3, AlertTriangle,
    Copy, Trash2, PauseCircle, PlayCircle, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { SiteCentricAgentCard } from "@/components/clients/SiteCentricAgentCard";
import { EnvBadge } from "@/components/clients/EnvBadge";

export default function ClientsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [includeDisabled, setIncludeDisabled] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            // Default scope (matches the backend default): superdomain +
            // active tenants only. Operators flip the toggle when they
            // need to surface disabled / archived clients (e.g. for a
            // re-enable workflow or audit review).
            const qs = includeDisabled ? `?include_disabled=true` : "";
            const { data } = await api.get(`/admin/clients${qs}`);
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [includeDisabled]);
    useEffect(() => {
        const cid = new URLSearchParams(window.location.search).get("client");
        if (cid) open(cid);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const open = async (cid) => {
        try {
            const { data } = await api.get(`/admin/clients/${cid}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    return (
        <div className="px-8 py-8" data-testid="clients-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Tenancy</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Clients</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Every tenant on the platform. SiteCentric provisions / disables / re-enables clients
                        and tunes their limits via the partner endpoints. API keys are managed inside each
                        client's detail.
                    </p>
                </div>
                <label
                    className="flex items-center gap-2 text-xs text-white/55 select-none cursor-pointer border border-white/10 rounded-full px-3 py-1.5 hover:border-white/25"
                    data-testid="clients-toggle-disabled-wrapper"
                >
                    <input
                        type="checkbox"
                        checked={includeDisabled}
                        onChange={(e) => setIncludeDisabled(e.target.checked)}
                        data-testid="clients-toggle-disabled"
                        className="accent-[#7C3AED]"
                    />
                    Show disabled
                </label>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
                {loading ? <div className="p-8 text-white/50">Loading…</div>
                    : items.length === 0 ? (
                        <div className="p-12 text-center text-white/50">
                            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            No clients yet.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] text-white/50">
                                <tr>
                                    <th className="text-left px-4 py-3 font-normal">Company</th>
                                    <th className="text-left px-4 py-3 font-normal">SiteCentric ID</th>
                                    <th className="text-left px-4 py-3 font-normal">Status</th>
                                    <th className="text-left px-4 py-3 font-normal">Active keys</th>
                                    <th className="text-left px-4 py-3 font-normal">Rate limit</th>
                                    <th className="text-left px-4 py-3 font-normal">Budget</th>
                                    <th className="text-left px-4 py-3 font-normal">Contact</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr
                                        key={c.client_id}
                                        data-testid={`client-row-${c.client_id}`}
                                        onClick={() => open(c.client_id)}
                                        className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {c.is_superdomain ? <Crown className="w-4 h-4 text-amber-400" />
                                                    : <Building2 className="w-4 h-4 text-white/40" />}
                                                <div>
                                                    <div className="font-medium flex items-center gap-2">{c.company_name || c.client_name}
                                                        {c.sitecentric_agent?.enabled && <EnvBadge environment={c.sitecentric_agent.environment} market={c.sitecentric_agent.market} testId={`client-env-badge-${c.client_id}`} />}
                                                    </div>
                                                    <div className="text-xs text-white/40 font-mono">{c.client_id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-white/60 font-mono text-xs">
                                            {c.sitecentric_company_id || <span className="text-white/30">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusPill status={c.status} superdomain={c.is_superdomain} />
                                        </td>
                                        <td className="px-4 py-3 tabular-nums">{c.active_keys || 0}</td>
                                        <td className="px-4 py-3 text-white/60">
                                            {c.limits?.rate_limit_per_minute ? `${c.limits.rate_limit_per_minute}/m` : <span className="text-white/30">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-white/60">
                                            {c.limits?.monthly_cost_budget_usd ? `$${c.limits.monthly_cost_budget_usd}/mo` : <span className="text-white/30">—</span>}
                                        </td>
                                        <td className="px-4 py-3 text-white/50 text-xs">{c.contact_email || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>

            {detail && <ClientDetailModal client={detail} onClose={() => setDetail(null)} onChange={() => { load(); open(detail.client_id); }} />}
        </div>
    );
}

function StatusPill({ status, superdomain }) {
    if (superdomain) {
        return <span data-testid="client-status-superdomain" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300 text-xs">
            <Crown className="w-3 h-3" /> superdomain
        </span>;
    }
    if (status === "active") {
        return <span data-testid="client-status-active" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-xs">
            <Power className="w-3 h-3" /> active
        </span>;
    }
    return <span data-testid="client-status-disabled" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-400/10 border border-rose-400/30 text-rose-300 text-xs">
        <Power className="w-3 h-3" /> disabled
    </span>;
}

function ClientDetailModal({ client, onClose, onChange }) {
    const [tab, setTab] = useState("overview");
    const [keys, setKeys] = useState([]);
    const [loadingKeys, setLoadingKeys] = useState(false);
    const [editLimits, setEditLimits] = useState(false);

    const loadKeys = async () => {
        setLoadingKeys(true);
        try {
            const { data } = await api.get(`/admin/clients/${client.client_id}/keys`);
            setKeys(data.items || []);
        } catch { /* ignore */ }
        finally { setLoadingKeys(false); }
    };

    useEffect(() => {
        if (tab !== "keys") return;
        loadKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, client.client_id]);

    const disable = async () => {
        const reason = window.prompt(`Reason to disable ${client.client_name}?`);
        if (reason === null) return;
        try {
            await api.post(`/admin/clients/${client.client_id}/disable`, { reason });
            toast.success("Client disabled — all active keys closed");
            onChange();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    const enable = async () => {
        try {
            await api.post(`/admin/clients/${client.client_id}/enable`);
            toast.success("Client re-enabled (review keys individually)");
            onChange();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const stats = client.stats || {};

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div
                className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-auto p-6"
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="client-detail-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        {client.is_superdomain ? <Crown className="w-6 h-6 text-amber-400" />
                            : <Building2 className="w-6 h-6 text-white/40" />}
                        <div>
                            <h2 className="font-display text-2xl">{client.company_name || client.client_name}</h2>
                            <p className="text-xs text-white/40 mt-1 font-mono">
                                {client.client_id}
                                {client.sitecentric_company_id && (
                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">
                                        SC: {client.sitecentric_company_id}
                                    </span>
                                )}
                            </p>
                        </div>
                        <StatusPill status={client.status} superdomain={client.is_superdomain} />
                        {client.sitecentric_agent?.enabled && <EnvBadge environment={client.sitecentric_agent.environment} market={client.sitecentric_agent.market} testId="client-detail-env-badge" />}
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>

                {!client.is_superdomain && (
                    <div className="flex gap-2 mb-4">
                        {client.status === "active" ? (
                            <button onClick={disable} data-testid="client-disable-btn"
                                className="px-3 py-1.5 rounded-full bg-rose-400/10 border border-rose-400/30 text-rose-300 text-xs hover:bg-rose-400/20">
                                <Power className="w-3 h-3 inline mr-1" /> Disable client
                            </button>
                        ) : (
                            <button onClick={enable} data-testid="client-enable-btn"
                                className="px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-xs hover:bg-emerald-400/20">
                                <RotateCw className="w-3 h-3 inline mr-1" /> Re-enable
                            </button>
                        )}
                        <button onClick={() => setEditLimits(true)} data-testid="client-edit-limits-btn"
                            className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10">
                            <Edit3 className="w-3 h-3 inline mr-1" /> Edit limits
                        </button>
                    </div>
                )}

                {client.is_superdomain && (
                    <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 mb-4 text-xs text-amber-300/80">
                        <div className="flex items-center gap-2 font-medium text-amber-300 mb-1">
                            <Shield className="w-4 h-4" /> SiteCentric superdomain
                        </div>
                        Read access scoped to <strong>training data</strong> and <strong>common knowledge</strong>.
                        Cannot read tenant-specific client data. Cannot be disabled.
                    </div>
                )}

                <div className="flex gap-2 border-b border-white/10 mb-4">
                    {[
                        ["overview", "Overview"],
                        ["keys", `API Keys (${stats.active_keys || 0})`],
                        ["limits", "Limits"],
                        ...(client.is_superdomain ? [] : [["sitecentric", "SiteCentric agent"]]),
                    ].map(([k, l]) => (
                        <button key={k} onClick={() => setTab(k)}
                            data-testid={`client-tab-${k}`}
                            className={`px-3 py-2 text-sm ${tab === k ? "text-white border-b-2 border-[#7C3AED]" : "text-white/40 hover:text-white/70"}`}
                        >{l}</button>
                    ))}
                </div>

                {tab === "overview" && (
                    <div>
                        <div className="grid grid-cols-4 gap-3 mb-4">
                            <Stat label="Active keys" value={stats.active_keys} />
                            <Stat label="CTAs" value={stats.ctas} />
                            <Stat label="Budgets" value={stats.budgets} />
                            <Stat label="Datalake items" value={stats.datalake_items} />
                        </div>
                        <Section title="Contact">
                            <div className="text-sm">{client.contact_email || <span className="text-white/40">—</span>}</div>
                        </Section>
                        <Section title="Created">
                            <div className="text-sm text-white/60">{client.created_at}</div>
                        </Section>
                        {client.disabled_at && (
                            <Section title="Disabled">
                                <div className="text-sm text-rose-400">{client.disabled_at}</div>
                                {client.disabled_reason && <div className="text-xs text-white/60 mt-1">{client.disabled_reason}</div>}
                            </Section>
                        )}
                    </div>
                )}

                {tab === "keys" && (
                    <KeysTab
                        keys={keys}
                        loading={loadingKeys}
                        clientId={client.client_id}
                        onChange={async () => { await loadKeys(); onChange(); }}
                    />
                )}

                {tab === "limits" && (
                    <LimitsTab client={client} onChange={onChange} />
                )}

                {tab === "sitecentric" && (
                    <SiteCentricAgentCard clientId={client.client_id} onChange={onChange} />
                )}
            </div>

            {editLimits && <LimitsEditModal client={client} onClose={() => setEditLimits(false)} onDone={() => { setEditLimits(false); onChange(); }} />}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-xs text-white/40">{label}</div>
            <div className="mt-1 text-xl font-medium tabular-nums">{value ?? 0}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-3">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-1">{title}</div>
            {children}
        </div>
    );
}

function KeysTab({ keys, loading, clientId, onChange }) {
    // Local UI state lives in this tab so it survives tab swaps without
    // re-fetching keys. ``revealed`` shows the plaintext + webhook
    // secret ONCE after a create/rotate; closing the modal forgets it
    // forever (matches Stripe's secret-handling UX).
    const [showCreate, setShowCreate] = useState(false);
    const [revealed, setRevealed] = useState(null);
    const [busyKeyId, setBusyKeyId] = useState(null);

    const reload = async () => {
        // Bubble up — the parent owns the keys array.
        await onChange();
    };

    const rotate = async (k) => {
        if (!window.confirm(
            `Rotate the plaintext key for "${k.name}"?\n\n`
            + "The previous plaintext stops working immediately. Update every consumer "
            + "with the new value shown on the next screen.",
        )) return;
        setBusyKeyId(k.id);
        try {
            const { data } = await api.post(`/admin/keys/${k.id}/rotate`);
            setRevealed({
                kind: "rotated",
                key_id: k.id,
                name: k.name,
                plaintext_key: data.plaintext_key,
                masked_key: data.masked_key,
            });
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Rotate failed");
        } finally {
            setBusyKeyId(null);
        }
    };

    const setStatus = async (k, status) => {
        setBusyKeyId(k.id);
        try {
            await api.patch(`/admin/keys/${k.id}`, { status });
            toast.success(status === "active" ? "Key re-enabled" : `Key ${status}`);
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
        } finally {
            setBusyKeyId(null);
        }
    };

    const remove = async (k) => {
        if (!window.confirm(
            `Revoke "${k.name}" permanently?\n\nThe key's plaintext stops working immediately. `
            + "Webhook deliveries stop. This cannot be undone — re-issuing requires creating a fresh key.",
        )) return;
        setBusyKeyId(k.id);
        try {
            await api.delete(`/admin/keys/${k.id}`);
            toast.success("Key revoked");
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Revoke failed");
        } finally {
            setBusyKeyId(null);
        }
    };

    const revealWebhookSecret = async (k) => {
        setBusyKeyId(k.id);
        try {
            const { data } = await api.get(`/admin/keys/${k.id}/webhook-secret`);
            setRevealed({
                kind: "webhook_secret",
                key_id: k.id,
                name: k.name,
                webhook_signing_secret: data.webhook_signing_secret,
                verification_hint: data.verification_hint,
                header_format: data.header_format,
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not reveal secret");
        } finally {
            setBusyKeyId(null);
        }
    };

    return (
        <div className="space-y-3" data-testid="client-keys-tab">
            <div className="flex items-center justify-between">
                <div className="text-xs text-white/40">
                    {keys.length === 0
                        ? "No API keys for this client yet."
                        : `${keys.length} key${keys.length === 1 ? "" : "s"} on this client.`}
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    data-testid="client-keys-create-btn"
                    className="px-3 py-1.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#c7a6ff] text-xs hover:bg-[#7C3AED]/25 flex items-center gap-1.5"
                >
                    <Plus className="w-3 h-3" /> Create key
                </button>
            </div>

            {loading ? (
                <div className="p-6 text-white/50">Loading keys…</div>
            ) : keys.length === 0 ? (
                <div className="p-8 text-center text-white/50 border border-dashed border-white/10 rounded-xl">
                    <Key className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No API keys for this client.
                    <p className="text-xs mt-2 text-white/40">Click <strong>Create key</strong> to mint one.</p>
                </div>
            ) : (
                <table className="w-full text-sm" data-testid="client-keys-table">
                    <thead className="text-white/50">
                        <tr>
                            <th className="text-left px-3 py-2 font-normal">Name</th>
                            <th className="text-left px-3 py-2 font-normal">Prefix</th>
                            <th className="text-left px-3 py-2 font-normal">Status</th>
                            <th className="text-right px-3 py-2 font-normal">Calls</th>
                            <th className="text-left px-3 py-2 font-normal">Last used</th>
                            <th className="text-right px-3 py-2 font-normal">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((k) => {
                            const isBusy = busyKeyId === k.id;
                            const isActive = k.status === "active";
                            return (
                                <tr key={k.id} className="border-t border-white/5" data-testid={`client-key-row-${k.id}`}>
                                    <td className="px-3 py-2">{k.name}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{k.prefix}…</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs ${isActive ? "text-emerald-400" : "text-rose-400"}`}>{k.status}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{k.usage_count || 0}</td>
                                    <td className="px-3 py-2 text-xs text-white/40">{(k.last_used_at || "").slice(0, 16) || "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex items-center justify-end gap-0.5">
                                            <IconBtn title="Rotate plaintext key" testId={`client-key-rotate-${k.id}`}
                                                onClick={() => rotate(k)} disabled={isBusy || k.status === "revoked"}>
                                                <RotateCw className="w-3.5 h-3.5" />
                                            </IconBtn>
                                            <IconBtn title="Reveal HMAC webhook secret" testId={`client-key-webhook-${k.id}`}
                                                onClick={() => revealWebhookSecret(k)} disabled={isBusy}>
                                                <KeyRound className="w-3.5 h-3.5" />
                                            </IconBtn>
                                            {isActive ? (
                                                <IconBtn title="Pause this key (refuses requests until re-enabled)" testId={`client-key-pause-${k.id}`}
                                                    onClick={() => setStatus(k, "paused")} disabled={isBusy}>
                                                    <PauseCircle className="w-3.5 h-3.5" />
                                                </IconBtn>
                                            ) : k.status === "paused" ? (
                                                <IconBtn title="Re-enable this key" testId={`client-key-resume-${k.id}`}
                                                    onClick={() => setStatus(k, "active")} disabled={isBusy}>
                                                    <PlayCircle className="w-3.5 h-3.5 text-emerald-400" />
                                                </IconBtn>
                                            ) : null}
                                            <IconBtn title="Revoke key permanently" testId={`client-key-revoke-${k.id}`}
                                                onClick={() => remove(k)} disabled={isBusy || k.status === "revoked"}
                                                className="hover:bg-rose-500/20 hover:text-rose-300">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </IconBtn>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {showCreate && (
                <CreateKeyModal
                    clientId={clientId}
                    onClose={() => setShowCreate(false)}
                    onCreated={(payload) => { setShowCreate(false); setRevealed(payload); reload(); }}
                />
            )}
            {revealed && <RevealModal data={revealed} onClose={() => setRevealed(null)} />}
        </div>
    );
}

function IconBtn({ children, title, onClick, disabled, testId, className = "" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            data-testid={testId}
            className={`p-1.5 rounded text-white/60 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent ${className}`}
        >{children}</button>
    );
}

function CreateKeyModal({ clientId, onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        rate_limit_per_minute: "",
        rate_limit_per_day: "",
        monthly_cost_budget_usd: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                client_id: clientId,
                rate_limit_per_minute: numOrNull(form.rate_limit_per_minute),
                rate_limit_per_day: numOrNull(form.rate_limit_per_day),
                monthly_cost_budget_usd: numOrNull(form.monthly_cost_budget_usd),
                notes: form.notes.trim() || null,
            };
            const { data } = await api.post(`/admin/keys`, payload);
            onCreated({
                kind: "created",
                key_id: data.id,
                name: data.name,
                plaintext_key: data.plaintext_key,
                masked_key: data.masked_key,
                webhook_signing_secret: data.webhook_signing_secret,
                verification_hint: "v1 = HMAC_SHA256(secret, f'{ts}.{json_body}')",
                header_format: "X-SiteCentric-Signature: t=<unix-ts>,v1=<hmac-sha256-hex>",
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Create failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-md p-6"
                onMouseDown={(e) => e.stopPropagation()} data-testid="client-key-create-modal">
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-display text-xl">Create API key</h3>
                    <button onClick={onClose}><X className="w-4 h-4 text-white/40" /></button>
                </div>
                <div className="space-y-3">
                    <Input label="Key name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Production webhook" testId="ck-create-name" />
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Rate / minute" hint="leave blank for default" value={form.rate_limit_per_minute} onChange={(v) => setForm({ ...form, rate_limit_per_minute: v })} type="number" testId="ck-create-rpm" />
                        <Input label="Rate / day" hint="leave blank for default" value={form.rate_limit_per_day} onChange={(v) => setForm({ ...form, rate_limit_per_day: v })} type="number" testId="ck-create-rpd" />
                    </div>
                    <Input label="Monthly budget (USD)" hint="hard cap; key pauses on overage" value={form.monthly_cost_budget_usd} onChange={(v) => setForm({ ...form, monthly_cost_budget_usd: v })} type="number" step="0.01" testId="ck-create-budget" />
                    <Input label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="optional context" testId="ck-create-notes" />
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm border border-white/10 rounded-md hover:bg-white/5">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="ck-create-submit"
                        className="px-3 py-1.5 text-sm bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white rounded-md disabled:opacity-50">
                        {saving ? "Creating…" : "Create key"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RevealModal({ data, onClose }) {
    // ONE-shot secret display. Closing forgets it — matches industry
    // standard for plaintext-key disclosure.
    const copy = (text, label) => {
        try {
            navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Clipboard unavailable — select and copy manually");
        }
    };

    const heading = data.kind === "created" ? "API key created"
        : data.kind === "rotated" ? "API key rotated"
        : "Webhook signing secret";

    return (
        <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-amber-400/30 rounded-2xl w-full max-w-2xl p-6"
                onMouseDown={(e) => e.stopPropagation()} data-testid="client-key-reveal-modal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2 text-amber-300 mb-1">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="label-xs">Shown ONCE — copy it now</span>
                        </div>
                        <h3 className="font-display text-xl">{heading}</h3>
                        <p className="text-xs text-white/40 mt-1 font-mono">{data.name}</p>
                    </div>
                    <button onClick={onClose}><X className="w-4 h-4 text-white/40" /></button>
                </div>
                {data.plaintext_key && (
                    <SecretRow label="Plaintext API key" value={data.plaintext_key} onCopy={() => copy(data.plaintext_key, "API key")} testId="reveal-plaintext" />
                )}
                {data.webhook_signing_secret && (
                    <SecretRow label="Webhook HMAC secret" value={data.webhook_signing_secret} onCopy={() => copy(data.webhook_signing_secret, "Webhook secret")} testId="reveal-webhook-secret" />
                )}
                {(data.verification_hint || data.header_format) && (
                    <div className="text-[11px] font-mono text-white/40 mt-3 space-y-1">
                        {data.header_format && <div><span className="text-white/55">Header:</span> {data.header_format}</div>}
                        {data.verification_hint && <div><span className="text-white/55">Verify:</span> {data.verification_hint}</div>}
                    </div>
                )}
                <div className="flex justify-end mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm bg-white/10 text-white rounded-md hover:bg-white/15"
                        data-testid="reveal-close">I've saved it</button>
                </div>
            </div>
        </div>
    );
}

function SecretRow({ label, value, onCopy, testId }) {
    return (
        <div className="mb-3">
            <div className="text-xs text-white/40 mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <code data-testid={testId} className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-xs font-mono break-all text-amber-200">{value}</code>
                <button onClick={onCopy} className="p-2 border border-white/10 rounded hover:bg-white/10 text-white/60">
                    <Copy className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, required, testId, placeholder, hint, type = "text", step }) {
    return (
        <div>
            <div className="text-xs text-white/60 mb-1">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""}
                data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
            {hint && <div className="text-[10px] text-white/30 mt-1">{hint}</div>}
        </div>
    );
}

function numOrNull(v) {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function LimitsTab({ client }) {
    const l = client.limits || {};
    return (
        <div className="grid grid-cols-2 gap-3" data-testid="client-limits-tab">
            <LimitCard label="Rate limit (per minute)" value={l.rate_limit_per_minute} unit="calls/min" />
            <LimitCard label="Monthly budget" value={l.monthly_cost_budget_usd} unit="USD/mo" />
            <LimitCard label="Max documents per request" value={l.max_documents_per_request} unit="docs" />
            <LimitCard label="Max file size" value={l.max_file_size_mb} unit="MB" />
            <LimitCard label="Expires at" value={l.expires_at} unit="" />
            <LimitCard
                label="Reuse charge override"
                value={l.reuse_charge_usd_per_doc}
                unit="USD/doc"
                hint={l.reuse_charge_usd_per_doc == null ? "uses global rate" : "per-tenant override"}
            />
        </div>
    );
}

function LimitCard({ label, value, unit, hint }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-xs text-white/40">{label}</div>
            <div className="mt-1 text-lg tabular-nums">
                {value != null && value !== "" ? `${value} ${unit}` : <span className="text-white/30 text-sm">unlimited</span>}
            </div>
            {hint ? <div className="text-[10px] font-mono text-white/30 mt-1">{hint}</div> : null}
        </div>
    );
}

function LimitsEditModal({ client, onClose, onDone }) {
    const [form, setForm] = useState({
        rate_limit_per_minute: client.limits?.rate_limit_per_minute ?? "",
        monthly_cost_budget_usd: client.limits?.monthly_cost_budget_usd ?? "",
        max_documents_per_request: client.limits?.max_documents_per_request ?? "",
        max_file_size_mb: client.limits?.max_file_size_mb ?? "",
        reuse_charge_usd_per_doc: client.limits?.reuse_charge_usd_per_doc ?? "",
        cascade_to_keys: true,
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const body = {};
            for (const k of ["rate_limit_per_minute", "monthly_cost_budget_usd", "max_documents_per_request", "max_file_size_mb", "reuse_charge_usd_per_doc"]) {
                body[k] = form[k] === "" ? null : Number(form[k]);
            }
            body.cascade_to_keys = form.cascade_to_keys;
            await api.patch(`/admin/clients/${client.client_id}/limits`, body);
            toast.success("Limits updated"); onDone();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const fields = [
        { key: "rate_limit_per_minute", step: "1", placeholder: "(blank = unlimited)" },
        { key: "monthly_cost_budget_usd", step: "0.01", placeholder: "(blank = unlimited)" },
        { key: "max_documents_per_request", step: "1", placeholder: "(blank = unlimited)" },
        { key: "max_file_size_mb", step: "1", placeholder: "(blank = unlimited)" },
        { key: "reuse_charge_usd_per_doc", step: "0.001", placeholder: "(blank = use global rate)", hint: "Per-document reuse charge override. Leave blank to use the global System Settings rate." },
    ];

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-md p-6"
                onMouseDown={(e) => e.stopPropagation()} data-testid="limits-edit-modal">
                <div className="flex items-start justify-between mb-4">
                    <h2 className="font-display text-xl">Edit limits</h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>
                <div className="space-y-3 text-sm">
                    {fields.map(({ key, step, placeholder, hint }) => (
                        <label key={key} className="block">
                            <div className="text-xs text-white/50 mb-1">{key.replace(/_/g, " ")}</div>
                            <input
                                type="number" step={step} value={form[key]}
                                data-testid={`limit-${key}`}
                                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                placeholder={placeholder}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                            />
                            {hint ? <div className="text-[10px] text-white/40 mt-1">{hint}</div> : null}
                        </label>
                    ))}
                    <label className="flex items-center gap-2 text-xs text-white/60 mt-3">
                        <input
                            type="checkbox" checked={form.cascade_to_keys}
                            data-testid="limit-cascade"
                            onChange={(e) => setForm({ ...form, cascade_to_keys: e.target.checked })}
                        />
                        Push limits to every active API key for this client
                    </label>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-white/60">Cancel</button>
                    <button onClick={submit} disabled={busy}
                        data-testid="limits-save-btn"
                        className="px-4 py-2 rounded-full bg-[#7C3AED] text-white text-sm disabled:opacity-50">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
